import { format, parseISO } from 'date-fns';
import { ptBR } from 'date-fns/locale';

/**
 * Utilitários de formatação de data/hora
 * Todas as funções usam o timezone local do navegador e formato 12h (AM/PM)
 * 
 * IMPORTANTE: Estas funções usam parseDateSafe() para evitar o bug de "um dia antes"
 * causado por new Date('YYYY-MM-DD') que interpreta como UTC meia-noite.
 */

/**
 * Parse seguro para datas - evita bug de timezone com YYYY-MM-DD
 * @param {Date|string} date - Data para parsear
 * @returns {Date|null} Date object ou null se inválido
 */
function parseDateSafe(date) {
  if (!date) return null;
  
  // Se já é Date, retorna
  if (date instanceof Date) {
    return isNaN(date.getTime()) ? null : date;
  }
  
  // Se é string
  if (typeof date === 'string') {
    // YYYY-MM-DD → parse como data local (não UTC!)
    if (/^\d{4}-\d{2}-\d{2}$/.test(date)) {
      const [year, month, day] = date.split('-').map(Number);
      return new Date(year, month - 1, day);
    }
    
    // ISO timestamp ou outro formato → usar parseISO
    return parseISO(date);
  }
  
  return null;
}

/**
 * Formata data e hora completa
 * @param {Date|string} date - Data para formatar
 * @returns {string} Ex: "15 de Janeiro de 2025 às 3:45 PM"
 */
export function formatDateTime(date) {
  const d = parseDateSafe(date);
  if (!d) return '';
  return format(d, "dd 'de' MMMM 'de' yyyy 'às' h:mm a", { locale: ptBR });
}

/**
 * Formata data e hora curta
 * @param {Date|string} date - Data para formatar
 * @returns {string} Ex: "15/01/2025 às 3:45 PM"
 */
export function formatDateTimeShort(date) {
  const d = parseDateSafe(date);
  if (!d) return '';
  return format(d, "dd/MM/yyyy 'às' h:mm a", { locale: ptBR });
}

/**
 * Formata data e hora em inglês
 * @param {Date|string} date - Data para formatar
 * @returns {string} Ex: "Jan 15, 2025 at 3:45 PM"
 */
export function formatDateTimeEN(date) {
  const d = parseDateSafe(date);
  if (!d) return '';
  return format(d, "MMM dd, yyyy 'at' h:mm a");
}

/**
 * Formata apenas a data
 * @param {Date|string} date - Data para formatar
 * @returns {string} Ex: "15 de Janeiro de 2025"
 */
export function formatDate(date) {
  const d = parseDateSafe(date);
  if (!d) return '';
  return format(d, "dd 'de' MMMM 'de' yyyy", { locale: ptBR });
}

/**
 * Formata apenas a data (curto)
 * @param {Date|string} date - Data para formatar
 * @returns {string} Ex: "15/01/2025"
 */
export function formatDateShort(date) {
  const d = parseDateSafe(date);
  if (!d) return '';
  return format(d, 'dd/MM/yyyy', { locale: ptBR });
}

/**
 * Formata data em inglês
 * @param {Date|string} date - Data para formatar
 * @returns {string} Ex: "Jan 15, 2025"
 */
export function formatDateEN(date) {
  const d = parseDateSafe(date);
  if (!d) return '';
  return format(d, 'MMM dd, yyyy');
}

/**
 * Formata apenas a hora
 * @param {Date|string} date - Data para formatar
 * @returns {string} Ex: "3:45 PM"
 */
export function formatTime(date) {
  const d = parseDateSafe(date);
  if (!d) return '';
  return format(d, 'h:mm a');
}

/**
 * Formata hora com segundos
 * @param {Date|string} date - Data para formatar
 * @returns {string} Ex: "3:45:30 PM"
 */
export function formatTimeWithSeconds(date) {
  const d = parseDateSafe(date);
  if (!d) return '';
  return format(d, 'h:mm:ss a');
}

/**
 * Formata data completa em formato longo
 * @param {Date|string} date - Data para formatar
 * @returns {string} Ex: "15 de Janeiro de 2025"
 */
export function formatDateLong(date) {
  const d = parseDateSafe(date);
  if (!d) return '';
  return format(d, "dd 'de' MMMM 'de' yyyy", { locale: ptBR });
}

/**
 * Formata data para input (yyyy-MM-dd)
 * @param {Date|string} date - Data para formatar
 * @returns {string} Ex: "2025-01-15"
 */
export function formatDateForInput(date) {
  const d = parseDateSafe(date);
  if (!d) return '';
  return format(d, 'yyyy-MM-dd');
}

/**
 * Formata data e hora para WhatsApp style
 * @param {Date|string} date - Data para formatar
 * @returns {object} { dateLabel: "January 15, 2025", time: "3:45 PM" }
 */
export function formatWhatsAppTimestamp(date) {
  const d = parseDateSafe(date);
  if (!d) return { dateLabel: '', time: '' };
  return {
    dateLabel: format(d, 'MMMM d, yyyy'),
    time: format(d, 'h:mm a')
  };
}

/**
 * Formata data para histórico de chamadas
 * @param {Date|string} date - Data para formatar
 * @returns {string} Ex: "Hoje 14:30", "Ontem 09:15", "15/11 18:45"
 */
export function formatCallDate(dateString) {
  const date = parseDateSafe(dateString);
  if (!date) return '';
  
  const today = new Date();
  const yesterday = new Date(today);
  yesterday.setDate(yesterday.getDate() - 1);
  
  const isToday = date.toDateString() === today.toDateString();
  const isYesterday = date.toDateString() === yesterday.toDateString();
  
  if (isToday) {
    return `Hoje ${format(date, 'HH:mm')}`;
  } else if (isYesterday) {
    return `Ontem ${format(date, 'HH:mm')}`;
  } else {
    return format(date, 'dd/MM HH:mm');
  }
}

/**
 * Formata duração de chamada em segundos
 * @param {number} seconds - Duração em segundos
 * @returns {string|null} Ex: "5m 23s", "45s", null se 0
 */
export function formatCallDuration(seconds) {
  if (!seconds || seconds === 0) return null;
  const mins = Math.floor(seconds / 60);
  const secs = seconds % 60;
  return mins > 0 ? `${mins}m ${secs}s` : `${secs}s`;
}
